import React, { useState, useEffect } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { loadGuideBySlug } from '../../utils/cmsContentLoader';
import { isGuideSlugAlias, resolveGuideSlug } from '../../data/guideSlugAliases';
import { getDestinations } from '../../api/destinationApi';
import { Seo, Loading, EmptyContent, ContentAdSlot } from '../shared';
import { GUIDES_INDEX_PATH, getGuideCategoryLabel } from '../../data/guideCategoryMeta';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import FaqAccordion from '../explore/FaqAccordion';
import DestinationCard from '../destinations/DestinationCard';
import GuideBodyText from './GuideBodyText';
import { LAST_VERIFIED } from '../../data/officialSources';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/guides.scss';
import '../../styles/explore.scss';
import '../../styles/destinations.scss';
import '../../styles/media.scss';

const CATEGORY_LABELS = {
	government: 'Government Hub', transportation: 'Transportation Hub', living_in_nepal: 'Living in Nepal Hub',
	seasonal: 'Seasonal Discovery', festival: 'Festivals', trends: 'Trending in Nepal',
	visa: 'Visa & Immigration', insurance: 'Travel Insurance', packing: 'Packing', food: 'Food & Dining',
	sim: 'SIM & Connectivity', money: 'Money & Payments', shopping: 'Shopping', safety: 'Safety',
	emergency: 'Emergency', culture: 'Culture', etiquette: 'Etiquette', permits: 'Permits',
	internet: 'Internet', health: 'Health', arrival: 'Arrival', trekking: 'Trekking', temples: 'Temples',
};
const CATEGORY_PATHS = {
	government: '/government', transportation: '/transportation', living_in_nepal: '/living-in-nepal',
	seasonal: '/seasonal-discovery', festival: '/festivals', trends: '/travel-trends',
	visa: '/government', insurance: '/government', packing: '/plan/packing', permits: '/government',
};

const HUB_IMAGES = {
	government: EXPLORE_IMAGES.valley,
	transportation: EXPLORE_IMAGES.annapurna,
	living_in_nepal: EXPLORE_IMAGES.valley,
	seasonal: EXPLORE_IMAGES.seasonal,
	festival: EXPLORE_IMAGES.festival,
	trends: EXPLORE_IMAGES.trekking,
};

const GUIDE_FAQS = {
	'best-time-to-visit-nepal': [
		{ q: 'Is October really the best month?', a: 'October and November generally offer the clearest mountain views and most stable trekking weather. March and April are the strong spring alternative with rhododendron blooms.' },
		{ q: 'Can I visit during monsoon?', a: 'Yes — for the right destinations. Upper Mustang and Manang sit in the rain shadow. Valley cultural trips work too. Classic high-altitude treks are miserable in monsoon.' },
	],
	'nepal-tourist-visa': [
		{ q: 'Can I get a visa on arrival?', a: 'Most nationalities can obtain a tourist visa on arrival at Tribhuvan International Airport, Pokhara International Airport, Gautam Buddha International Airport, and designated land entry points. Check immigration.gov.np for nationality eligibility.' },
		{ q: 'What are the current visa fees?', a: 'Per the Department of Immigration: 15 days USD 30, 30 days USD 50, 90 days USD 125 (multiple entry). Confirm on the official site before travel.' },
		{ q: 'How long can I stay?', a: 'Tourist visas can be extended up to a maximum of 150 days in a calendar year. Initial visas are issued for 15, 30, or 90 days.' },
	],
};

function GuideDetailPage() {
	const { slug } = useParams();
	const canonicalSlug = resolveGuideSlug(slug);
	const [guide, setGuide] = useState(null);
	const [relatedDestinations, setRelatedDestinations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		setLoading(true);
		setNotFound(false);
		loadGuideBySlug(slug)
			.then((data) => {
				if (data) setGuide(data);
				else setNotFound(true);
			})
			.catch(() => setNotFound(true))
			.finally(() => setLoading(false));
	}, [slug]);

	useEffect(() => {
		if (!guide) return;
		getDestinations({ per_page: 4 })
			.then((res) => setRelatedDestinations((res.data && res.data.destinations) || []))
			.catch(() => setRelatedDestinations([]));
	}, [guide]);

	if (isGuideSlugAlias(slug)) {
		return <Redirect to={`/guides/${canonicalSlug}`} />;
	}

	if (loading) return <Loading />;

	if (notFound || !guide) {
		return (
			<div className="container py-5">
				<EmptyContent>
					<i className="fas fa-book text-muted" style={{ fontSize: '2rem' }} aria-hidden="true" />
					<h3 className="mt-3">Guide not found</h3>
					<Link to="/destinations" className="btn btn-primary mt-2">Explore Nepal</Link>
				</EmptyContent>
			</div>
		);
	}

	const g = guide;
	const hubPath = CATEGORY_PATHS[g.category] || '/destinations';
	const hubLabel = CATEGORY_LABELS[g.category] || 'Guides';
	const faqs = GUIDE_FAQS[g.slug] || [];
	const heroImage = HUB_IMAGES[g.category] || EXPLORE_IMAGES.himalaya;

	return (
		<div className="explore-page guide-detail-page">
			<Seo title={g.title} path={`/guides/${canonicalSlug}`} description={g.summary} />

			<ExploreHero
				image={heroImage}
				eyebrow={hubLabel}
				title={g.title}
				lead={g.summary}
				compact
			/>

			<QuickFactsBar facts={[
				{ value: getGuideCategoryLabel(g.category), label: 'Category' },
				{ value: g.official_source_verified_at ? 'Verified' : (g.subject_to_change ? 'Confirm' : 'Stable'), label: 'Status' },
				{ value: g.official_source_url ? 'Cited' : 'Editorial', label: 'Source type' },
			]} />

			<div className="container explore-body" style={{ paddingTop: '2rem' }}>
				<nav className="guide-breadcrumb mb-3" aria-label="Breadcrumb">
					<Link to={GUIDES_INDEX_PATH}>Travel Guide</Link>
					{' / '}
					<Link to={`${GUIDES_INDEX_PATH}#category-${g.category}`}>{getGuideCategoryLabel(g.category)}</Link>
					{' / '}
					<span>{g.title}</span>
				</nav>

				<OfficialCitation
					sourceName={g.official_source_name}
					sourceUrl={g.official_source_url}
					lastVerified={g.official_source_verified_at || (g.official_source_url ? LAST_VERIFIED : null)}
					subjectToChange={g.subject_to_change || !g.official_source_url}
				/>

				<div className="editorial-columns">
					<article className="guide-detail" style={{ maxWidth: 'none', padding: 0 }}>
						<GuideBodyText text={g.body} style={{ fontSize: '1.05rem' }} />
					</article>
					<aside>
						<div className="info-card">
							<div className="info-card__image">
								<img src={heroImage} alt="" loading="lazy" />
							</div>
							<div className="info-card__body">
								<p className="info-card__label">Keep exploring</p>
								<h3 className="info-card__title">Related hubs</h3>
								<div className="related-rail" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
									<Link to="/seasonal-discovery" className="related-link">Seasonal Discovery</Link>
									<Link to="/government" className="related-link">Government &amp; visas</Link>
									<Link to="/transportation" className="related-link">Transportation</Link>
									<Link to="/festivals" className="related-link">Festivals</Link>
									<Link to="/destinations" className="related-link">All destinations</Link>
								</div>
							</div>
						</div>
					</aside>
				</div>

				<ContentAdSlot layout="guide" className="adsense-unit--in-content my-4" />

				{faqs.length > 0 && (
					<div style={{ paddingTop: '2rem' }}>
						<DiscoverySection title="Frequently asked questions">
							<FaqAccordion items={faqs} />
						</DiscoverySection>
					</div>
				)}

				{relatedDestinations.length > 0 && (
					<DiscoverySection
						eyebrow="Places to visit"
						title="Related destinations"
						intro="Put this guide into practice — explore destinations with season, difficulty, and practical details."
					>
						<div className="destination-rail">
							{relatedDestinations.map((d) => (
								<DestinationCard key={d.slug} destination={d} />
							))}
						</div>
					</DiscoverySection>
				)}

				<div className="explore-cta-band mt-4">
					<h3>Planning a trip?</h3>
					<p>Browse destinations by interest, season, and province — each page answers the questions you'd ask before committing your dates.</p>
					<Link to="/destinations" className="explore-hero__cta explore-hero__cta--primary">Explore Nepal</Link>
				</div>
			</div>
		</div>
	);
}

export default GuideDetailPage;
